import LoadingCircle from "reports/components/simple/LoadingCircle";
import EOSReport from "../../../../components/Reports/EOSReport";
import PciukSummaryMetrics from "./PciukSummaryMetrics";
import { round, isEmpty } from "lodash";
import MetricAndPnLBreakdowns from "../../../../components/Reports/GenericCharts/MetricAndPnLBreakdowns/MetricAndPnLBreakdowns";
import {
  getMaxTagDurationMs,
  useGeAndLtFromUrl,
  useProcessGroupNameAndMachemetryIds,
} from "../../../../components/Reports/reportUtils";
import PnLChart from "../../../../components/Reports/GenericCharts/PnLChart/PnLChart";
import { getPnlTimesWithProducingLosses } from "../../../../components/Reports/GenericCharts/PnLChart/pnlChartUtils";
import useEosData from "reports/store/useEosData";
import { MetricEnum, MetricUnitsEnum } from "reports/utils/metricTypes";
import { getPciukMetrics } from "./pciukEosUtils";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS } from "reports/utils/timeUtils";
import Pareto from "reports/components/Reports/BespokeReports/Pareto";
import { PnlCategoriesEnum } from "reports/types";
import MetricPerProcessChart from "reports/components/Reports/GenericCharts/MetricPerProcessChart";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export default function PciukEos() {
  const { processGroupName, machemetryIds } =
    useProcessGroupNameAndMachemetryIds({});

  // EOS needs ge and lt url params, sample is December 9, 2020, 8AM - 4PM
  const sampleLink = `/${processGroupName}?ge=1607518800000&lt=1607547600000`;

  return (
    <>
      {machemetryIds && machemetryIds.length > 0 ? (
        <EOSReport
          machemetryIds={machemetryIds}
          sampleLink={sampleLink}
          flightTimelineLinkGroup={
            !processGroupName || processGroupName?.toLowerCase() === "all"
              ? undefined
              : processGroupName.includes("1")
                ? "01"
                : "02"
          }
          flightDashboardLinkGroup="all"
          shouldShowPrint={false}
          shouldSkipSkuData={false}
        >
          <PciukEosComponents {...{ processGroupName, machemetryIds }} />
        </EOSReport>
      ) : (
        <LoadingCircle
          logAfterTimeout={{
            logData: { machemetryIds },
            delay: 5 * 1000,
          }}
        />
      )}
    </>
  );
}

function PciukEosComponents({ processGroupName, machemetryIds }) {
  const { data } = useEosData();
  const { ge, lt } = useGeAndLtFromUrl();
  const durationMs = lt - ge;
  const reportConfig = useReportConfig();

  const isWeekly = durationMs > ONE_DAY_OF_MS * 5;

  // As of OPS-43653, we show top downtimes for all reports, not just 24 hour and weekly
  const showTopDowntimes = true;

  if (data && !isEmpty(data) && machemetryIds && machemetryIds.length > 0) {
    const maxTagDurationUnplannedWaiting = getMaxTagDurationMs({
      pnlBreakdown: data,
      categories: [PnlCategoriesEnum.unplanned, PnlCategoriesEnum.waiting],
    });
    const xAxisDomain = [
      0,
      round((maxTagDurationUnplannedWaiting ?? 0) / ONE_HOUR_OF_MS, 0),
    ];

    const topDowntimesProps = {
      pnlBreakdown: data,
      height: 500,
      xLabel: "Downtimes",
      timeDivisor: ONE_HOUR_OF_MS,
      numberOfDowntimes: 5,
      showDurationLabel: true,
      shouldShowHideLessThan1Percent: false,
      xAxisDomain,
    };

    return (
      <>
        <PciukSummaryMetrics {...{ data, processGroupName }} />
        {showTopDowntimes && (
          <>
            <Pareto
              categoriesToSkip={[
                PnlCategoriesEnum.planned,
                PnlCategoriesEnum.waiting,
                PnlCategoriesEnum.unknown,
              ]}
              title={`Top ${topDowntimesProps.numberOfDowntimes} Unplanned Downtimes`}
              {...topDowntimesProps}
            />
            <Pareto
              categoriesToSkip={[
                PnlCategoriesEnum.unplanned,
                PnlCategoriesEnum.planned,
                PnlCategoriesEnum.unknown,
              ]}
              title={`Top ${topDowntimesProps.numberOfDowntimes} Waiting Downtimes`}
              {...topDowntimesProps}
            />
          </>
        )}
        {isWeekly && (
          <MetricPerProcessChart
            summaryMetricInput={{
              name: MetricEnum.OEE,
              title: "Line Efficiency",
            }}
          />
        )}
        <PnLChart pnlTimes={getPnlTimesWithProducingLosses({ data })} />
        <MetricAndPnLBreakdowns
          {...{
            machemetryIds,
          }}
          summaryMetricsInputList={
            reportConfig?.eosReportMetricsConfig?.summaryMetricsInputList ?? [
              {
                name: MetricEnum.OEE,
                title: "Line Eff.",
                order: 0,
              },
              {
                name: MetricEnum.PERF,
                order: 1,
              },
              {
                name: MetricEnum.OPERATION_TIME,
                title: "Production",
                order: 3,
              },
              { name: MetricEnum.COUNT, order: 4 },
            ]
          }
          getCustomSummaryMetricsInputListWithPnlBreakdown={pnlBreakdown => {
            const { engineeringDowntime } = getPciukMetrics(pnlBreakdown);

            return [
              {
                title: "Eng. Downtime",
                customValue: engineeringDowntime,
                unit: MetricUnitsEnum.percent,
                // Requested order for metrics was to put engineeringDowntime after performance
                order: 2,
              },
            ];
          }}
        />
      </>
    );
  }
  return (
    <LoadingCircle
      logAfterTimeout={{
        logData: { eosData: data, machemetryIds },
        delay: 5 * 1000,
      }}
    />
  );
}
